import React, { useState } from 'react';
import { Button, Table, Row } from 'react-bootstrap';
import KaAlert from '../Common/KaAlert'
import { useUser } from '../UserAccess/useUser';
import useDisplayMessage from '../Common/useDisplayMessage';
import useUserRequest, { UserRequestSpec } from '../Common/useUserRequest';
import useGetUsers, { BusinessSiteDto, BusinessUserDto, UserRoleDto } from '../Requests/useGetUsers';
import { enabledPermissionCount, totalPermissionCount } from '../Common/permissions';
import { useSelectedBusiness } from '../UserAccess/useSelectedBusiness';
import { SiteChip } from '../Common/SiteChip';
import { CreateEditUser } from './CreateEditUser';
import { RoleModal } from './RoleModal';
import Confirm from '../Common/Confirm';
import { HttpMethod } from '../Common/useFetch';
import BoldButton from '../Common/BoldButton';
import QueryView from '../Common/QueryView';
import FilterInput, { textSatisfiesFilter } from '../FilterInput';
import OverflowTooltip from '../Common/OverflowTooltip';
import InfoTooltip from '../Common/InfoTooltip';

const removeSpec = (businessId?: string, userId?: string): UserRequestSpec => ({
  path: `${baseUserUrl(businessId, userId)}`,
  method: HttpMethod.DELETE,
});

const baseUserUrl = (businessId?: string, userId?: string) => `${baseUrl(businessId)}/${userId}`;

const baseUrl = (businessId?: string) => `/api/businesses/${businessId}/users`;

const sortRoles = (roles: UserRoleDto[]): UserRoleDto[] =>
  [...roles].sort((a, b) => Number(b.administrator) - Number(a.administrator) || a.name.localeCompare(b.name));

const sortUsers = (users: BusinessUserDto[]): BusinessUserDto[] =>
  [...users].sort((a, b) => a.emailAddress.localeCompare(b.emailAddress));

const sortSites = (sites: BusinessSiteDto[]): BusinessSiteDto[] =>
  [...sites].sort((a, b) => a.name.localeCompare(b.name));

const UserPage = () => {
  const { id: currentUserId, isDevelopment } = useUser();
  const business = useSelectedBusiness();
  const displayMessage = useDisplayMessage();
  const getUsers = useGetUsers();
  const [userToRemove, setUserToRemove] = useState<BusinessUserDto | null>(null);
  const [role, setRole] = useState<UserRoleDto | 'create' | null>(null);
  const [filterText, setFilterText] = useState('');

  const [editing, setEditing] = useState<BusinessUserDto | 'create' | null>(null);

  const addClicked = () => setEditing('create');

  const editClicked = (businessUser: BusinessUserDto) => setEditing(businessUser);

  const editUserDismissed = () => setEditing(null);

  const editSuccess = (emailAddress: string) => {
    displayMessage.success(editing === 'create'
      ? `Added ${emailAddress} to business: ${business.name}`
      : `${emailAddress} updated.`);
    getUsers.query();
    setEditing(null);
  };

  const createRoleClicked = () => setRole('create');

  const editRoleClicked = (userRole: UserRoleDto) => setRole(userRole);

  const roleModalDismissed = () => setRole(null);

  const roleSaved = (name: string) => {
    displayMessage.success(role === 'create' ? `Role ${name} created.` : `${name} updated.`);
    getUsers.query();
    setRole(null);
  };

  const [confirmRemoveVisible, setConfirmRemoveVisible] = useState(false);

  const remove = useUserRequest(
    removeSpec(business.id, userToRemove?.id), {
    onComplete: () => setConfirmRemoveVisible(false),
    onSuccess: () => {
      displayMessage.success(`${userToRemove?.emailAddress} removed.`);
      getUsers.query();
    },
    onError: (err) => displayMessage.fail(err.message)
  });

  const removeClicked = (businessUser: BusinessUserDto) => {
    setUserToRemove(businessUser);
    setConfirmRemoveVisible(true);
  };

  const confirmRemoveDismissed = () => setConfirmRemoveVisible(false);

  const removalConfirmed = () => remove.request(null);

  return <QueryView
    query={getUsers}
    renderData={({ userRoles, users, sites: unsortedSites }) => {
      const sites = sortSites(unsortedSites);

      return (<>
        {editing && <CreateEditUser
          key={editing === 'create' ? 'create' : editing.id}
          businessUser={editing === 'create' ? null : editing}
          roles={userRoles}
          sites={sites}
          onHide={editUserDismissed}
          onSuccess={editSuccess} />}
        {role && <RoleModal
          role={role === 'create' ? undefined : role}
          onHide={roleModalDismissed}
          onSuccess={roleSaved} />}
        <Confirm
          visible={confirmRemoveVisible}
          title='Delete User'
          body={<>
            Are you sure that you want to permanently remove
            <span className="d-block text-truncate fw-bold" title={userToRemove?.emailAddress}>{userToRemove?.emailAddress}</span>
          </>}
          isLoading={remove.isLoading}
          onDismiss={confirmRemoveDismissed}
          onConfirm={removalConfirmed} />
        <Row className="mb-2 justify-content-between align-items-center">
          <h2 className="col-auto ka-blue">User Management</h2>
          <BoldButton className="col-auto btn-ghost-primary me-4" onClick={createRoleClicked}>Create Role</BoldButton>
        </Row>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <Row className="g-3 mb-4">
          {sortRoles(userRoles).map(role => {
            const userCount = users.filter(u => u.userRoleId === role.id).length;
            return (
              <div className="col-md-4" key={role.id}>
                <div className="card h-100 border border-2">
                  <div className="card-body px-3 py-2 d-flex flex-column">
                    <div className="d-flex justify-content-between gap-2 mt-2">
                      <div className="overflow-hidden">
                        <div className="d-flex align-items-center gap-2">
                          <h4 className="card-title mb-1 text-truncate" title={role.name}>{role.name}</h4>
                          {role.administrator &&
                            <InfoTooltip content={<>
                              Administrators have full access to every feature and are the only users
                              who can manage users and sites.<br />
                              <br />
                              This role cannot be edited or deleted.
                            </>} />}
                        </div>
                        <OverflowTooltip className="card-text text-muted small mt-2 mb-0" text={role.description} lines="2" />
                      </div>
                      <span className="badge bg-soft-primary text-primary flex-shrink-0 align-self-start">
                        {userCount} {userCount === 1 ? 'user' : 'users'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-end mt-auto pt-2 pb-1">
                      <div className="card-text text-muted small">
                        <em>{enabledPermissionCount(role)} of {totalPermissionCount} permissions</em>
                      </div>
                      <div>
                        {role.administrator
                          ? <i className="bi bi-lock-fill text-muted fs-3" title="Locked" />
                          : <BoldButton
                            className="btn-ghost-secondary btn-sm p-0"
                            title="Edit role"
                            onClick={() => editRoleClicked(role)}>
                            <i className="bi bi-pencil-square fs-3 px-1" />
                          </BoldButton>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>);
          })}
        </Row>
        <hr className="my-4" />
        <Row className="justify-content-between align-items-center mb-2">
          <div className="col-md-4">
            <FilterInput
              placeholder="Search users..."
              value={filterText}
              size="md"
              onFilterTextChange={setFilterText} />
          </div>
          <BoldButton className="col-auto btn-ghost-primary me-4" onClick={addClicked}>Add User</BoldButton>
        </Row>
        <Table borderless>
          <thead>
            <tr className="border-bottom border-dark border-1">
              <th className="col-6">User</th>
              <th className={isDevelopment ? 'col-2' : 'col-5'}>Role</th>
              {isDevelopment && <th className="col-3">Sites</th>}
              <th className="col-1"></th>
            </tr>
          </thead>
          <tbody>
            {sortUsers(users).filter(u => textSatisfiesFilter(filterText)(u.emailAddress)).map(businessUser =>
              <tr key={businessUser.id} className="border-top">
                <td className="py-2 align-middle" style={{ maxWidth: 0 }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className="avatar avatar-sm avatar-circle avatar-soft-primary p-0 flex-shrink-0">
                      <span className="avatar-initials">{businessUser.emailAddress.charAt(0).toUpperCase()}</span>
                    </span>
                    <OverflowTooltip className="mb-0" text={businessUser.emailAddress} />
                    {!businessUser.hasSignedIn && <span className="badge bg-soft-warning text-dark border border-warning flex-shrink-0">Invite pending</span>}
                  </div>
                </td>
                <td className="py-2 align-middle" style={{ maxWidth: 0 }}>
                  <span className="badge bg-soft-primary text-primary mw-100 text-wrap text-break text-start lh-sm" title={userRoles.find(r => r.id === businessUser.userRoleId)?.name}>
                    {userRoles.find(r => r.id === businessUser.userRoleId)?.name}
                  </span>
                </td>
                {isDevelopment &&
                  <td className="py-2 align-middle" style={{ maxWidth: 0 }}>
                    <SiteChip
                      siteRestricted={businessUser.siteRestricted}
                      siteNames={sites.filter(s => businessUser.siteIds.includes(s.id)).map(s => s.name)}
                      totalSiteCount={sites.length} />
                  </td>}
                <td className="py-2 align-middle">
                  {businessUser.id !== currentUserId &&
                    <div className="dropdown">
                      <Button className='dropdown-toggle btn-ghost-secondary' variant='link' id='actionsMenuButton' data-bs-toggle='dropdown'>
                        <strong>Actions</strong>
                      </Button>
                      <div className='dropdown-menu'>
                        <BoldButton className='dropdown-item btn-ghost-secondary' onClick={() => editClicked(businessUser)}>Edit User</BoldButton>
                        <BoldButton className='dropdown-item btn-ghost-secondary' onClick={() => removeClicked(businessUser)}>Delete User</BoldButton>
                      </div>
                    </div>}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </>);
    }} />;
}

export { UserPage }
